
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import api from '../services/api';
import { toast } from 'react-toastify';
import { APP_VERSION } from '../utils/version';
import NoTranslate from '../components/NoTranslate';
import { useLeaveTypes } from '../hooks/useLeaveTypes';
import {
  formatDisplayDate,
  formatDisplayDateTime,
  resolveLeaveTypeLabel,
  resolveLeaveTypeName
} from '../utils/localeFormat';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

function AdminDashboard() {
  const leaveTypes = useLeaveTypes();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/dashboard/admin');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Admin dashboard data fetch error:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickApproval = async (applicationId, status) => {
    try {
      await api.patch(`/applications/${applicationId}/review`, {
        status: status,
        admin_notes: `${status === 'approved' ? 'Approved' : 'Rejected'} from Admin Dashboard`
      });
      
      toast.success(`Application ${status === 'approved' ? 'approved' : 'rejected'} successfully`);
      fetchDashboardData(); // Reload data
      
    } catch (error) {
      console.error('Application processing error:', error);
      toast.error('Failed to process application');
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!dashboardData) {
    return <div className="alert alert-danger">Failed to load data</div>;
  }

  const { departmentStats, assignedPendingApplications, pendingApplications, leaveTypeStats } = dashboardData;
  const showAssignedSection = Array.isArray(assignedPendingApplications);

  const renderPendingSection = (title, applications, viewAllTo, variant = 'default') => (
    <div className={`card mb-4 pending-section${variant === 'assigned' ? ' pending-section--assigned' : ''}`}>
      <div className="card-header">
        <h3>{title} ({applications.length})</h3>
      </div>
      {applications.length > 0 ? (
        <div className="pending-applications">
          {applications.slice(0, 5).map(app => (
            <div key={app.id} className="pending-item">
              <div className="applicant-info">
                <div className="applicant-name">
                  <NoTranslate>
                    {app.applicant_name} ({app.employee_id})
                  </NoTranslate>
                </div>
                <NoTranslate as="div" className="applicant-dept">{app.department}</NoTranslate>
              </div>

              <div className="application-info">
                <NoTranslate as="div" className="leave-type">
                  {resolveLeaveTypeName(app, leaveTypes)}
                </NoTranslate>
                <div className="leave-dates">
                  {formatDate(app.start_date)} - {formatDate(app.end_date)}
                  <span className="leave-hours">({app.total_hours}h)</span>
                </div>
                <div className="applied-date">
                  Applied: {formatDateTime(app.applied_at)}
                </div>
              </div>

              <div className="quick-actions">
                <button
                  className="btn btn-success btn-sm"
                  onClick={() => handleQuickApproval(app.id, 'approved')}
                >
                  Approve
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleQuickApproval(app.id, 'rejected')}
                >
                  Reject
                </button>
                <Link
                  to={`/admin/applications?id=${app.id}`}
                  className="btn btn-outline-primary btn-sm"
                >
                  Details
                </Link>
              </div>
            </div>
          ))}

          {applications.length > 5 && (
            <div className="view-all-pending">
              <Link to={viewAllTo} className="btn btn-outline-primary">
                View All ({applications.length})
              </Link>
            </div>
          )}
        </div>
      ) : (
        <div className="no-pending">
          <p className="text-muted">No pending applications</p>
        </div>
      )}
    </div>
  );

  // Department statistics chart
  const departmentChartData = {
    labels: departmentStats.map(dept => dept.department),
    datasets: [
      {
        label: 'Pending Applications',
        data: departmentStats.map(dept => dept.pending_applications),
        backgroundColor: '#ffc107',
        borderRadius: 4
      },
      {
        label: 'Average Leave Days Used',
        data: departmentStats.map(dept => parseFloat(dept.avg_used_days || 0)),
        backgroundColor: '#007bff',
        borderRadius: 4
      }
    ]
  };

  // Leave type statistics chart
  const leaveTypeChartData = {
    labels: leaveTypeStats.map(stat => resolveLeaveTypeLabel(stat.leave_type_name, leaveTypes)),
    datasets: [{
      data: leaveTypeStats.map(stat => stat.application_count || 0),
      backgroundColor: leaveTypeStats.map(stat => stat.color_code || '#6c757d'),
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  const formatDate = (dateString) => {
    return formatDisplayDate(dateString, {
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return formatDisplayDateTime(dateString, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1>Administrator Dashboard</h1>
          <small className="text-muted">{APP_VERSION}</small>
        </div>
        <div className="admin-actions">
          <Link to="/admin/applications" className="btn btn-primary">
            Application Management
          </Link>
          <Link to="/admin/leave-balance" className="btn btn-secondary">
            Leave Management
          </Link>
        </div>
      </div>

      {showAssignedSection && renderPendingSection(
        'Assigned to You',
        assignedPendingApplications,
        '/approver',
        'assigned'
      )}

      {renderPendingSection(
        showAssignedSection ? 'All Pending Applications' : 'Pending Applications',
        pendingApplications,
        '/admin/applications?status=pending'
      )}

      {/* Statistics Charts */}
      <div className="grid grid-2 mb-4">
        <div className="card">
          <h3>Statistics by Department</h3>
          <div style={{ height: '300px' }}>
            <Bar 
              data={departmentChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: 'top' },
                  tooltip: {
                    callbacks: {
                      label: (context) => {
                        const label = context.dataset.label;
                        const value = context.parsed.y;
                        return label.includes('Days') ? `${label}: ${value} days` : `${label}: ${value}`;
                      }
                    }
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      stepSize: 1
                    }
                  }
                }
              }}
            />
          </div>
        </div>

        <div className="card">
          <h3>Applications by Leave Type</h3>
          <div style={{ width: '300px', height: '300px', margin: '0 auto' }}>
            <Doughnut 
              data={leaveTypeChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: 'bottom' },
                  tooltip: {
                    callbacks: {
                      label: (context) => {
                        const label = context.label;
                        const value = context.parsed;
                        return `${label}: ${value}`;
                      }
                    }
                  }
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* Detailed Statistics by Department */}
      <div className="card">
        <h3>Detailed Statistics by Department</h3>
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Department</th>
                <th>Employees</th>
                <th>Total Applications</th>
                <th>Pending</th>
                <th>Avg. Leave Days Used</th>
                <th>Leave Usage Rate</th>
              </tr>
            </thead>
            <tbody>
              {departmentStats.map(dept => (
                <tr key={dept.department}>
                  <td><NoTranslate>{dept.department}</NoTranslate></td>
                  <td>{dept.total_employees}</td>
                  <td>{dept.total_applications}</td>
                  <td>
                    <NoTranslate className={`badge ${dept.pending_applications > 0 ? 'badge-warning' : 'badge-success'}`}>
                      {dept.pending_applications}
                    </NoTranslate>
                  </td>
                  <td>{parseFloat(dept.avg_used_days || 0).toFixed(1)} days</td>
                  <td>
                    <div className="progress-wrapper">
                      <div className="progress">
                        <div 
                          className="progress-bar" 
                          style={{ width: `${Math.min(dept.avg_usage_rate || 0, 100)}%` }}
                        ></div>
                      </div>
                      <span className="progress-text">{dept.avg_usage_rate || 0}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        .admin-actions {
          display: flex;
          gap: 0.5rem;
        }
        
        .card-header {
          background-color: #f8f9fa;
          padding: 1rem;
          border-bottom: 1px solid #dee2e6;
          margin: -20px -20px 20px -20px;
        }
        
        .card-header h3 {
          margin: 0;
          color: #495057;
        }

        .pending-section--assigned .card-header {
          background-color: #fff8e1;
          border-bottom-color: #ffe082;
        }
        
        .pending-applications {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        
        .pending-item {
          display: grid;
          grid-template-columns: 200px 1fr auto;
          gap: 1rem;
          align-items: center;
          padding: 1rem;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          background-color: #f8f9fa;
        }
        
        .applicant-info {
          display: flex;
          flex-direction: column;
        }
        
        .applicant-name {
          font-weight: bold;
          color: #495057;
        }
        
        .applicant-dept {
          font-size: 0.9rem;
          color: #6c757d;
        }
        
        .application-info {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        
        .leave-type {
          font-weight: 500;
          color: #007bff;
        }
        
        .leave-dates {
          color: #495057;
        }
        
        .leave-hours {
          font-size: 0.9rem;
          color: #6c757d;
          margin-left: 0.5rem;
        }
        
        .applied-date {
          font-size: 0.8rem;
          color: #6c757d;
        }
        
        .quick-actions {
          display: flex;
          gap: 0.5rem;
          flex-direction: column;
        }
        
        .btn-sm {
          padding: 0.25rem 0.5rem;
          font-size: 0.875rem;
        }
        
        .view-all-pending {
          text-align: center;
          padding: 1rem;
          border-top: 1px solid #dee2e6;
          margin-top: 1rem;
        }
        
        .no-pending {
          text-align: center;
          padding: 2rem;
        }
        
        .progress-wrapper {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .progress {
          flex: 1;
          height: 20px;
          background-color: #e9ecef;
          border-radius: 10px;
          overflow: hidden;
        }
        
        .progress-bar {
          height: 100%;
          background-color: #007bff;
          border-radius: 10px;
          transition: width 0.3s ease;
        }
        
        .progress-text {
          font-size: 0.9rem;
          font-weight: 500;
          min-width: 40px;
          text-align: right;
        }
        
        .table-responsive {
          overflow-x: auto;
        }
        
        @media (max-width: 768px) {
          .admin-actions {
            flex-direction: column;
          }
          
          .pending-item {
            grid-template-columns: 1fr;
            gap: 0.5rem;
          }
          
          .quick-actions {
            flex-direction: row;
            justify-content: center;
          }
          
          .progress-wrapper {
            flex-direction: column;
            gap: 0.25rem;
          }
          
          .progress-text {
            min-width: auto;
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
}

export default AdminDashboard;
